import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import type { Env } from '@fleet-cert-console/shared';
import { vpnTerminalSession, vpnTerminalSessionClose } from '../api/client.js';
import { VpnFilePushModal } from '../components/VpnFilePushModal.js';
import { SsmTerminal } from '../lib/ssmTerminal.js';

const envFromParam = (value: string | null): Env => (value === 'prod' ? 'prod' : 'dev');

type ConnState = 'connecting' | 'connected' | 'closed';

/**
 * Full-window terminal to one gateway (/vpn/:gatewayId/terminal) - opened in its own tab from
 * Connect. Session state machine: connecting -> connected -> closed (idle timeout, network drop,
 * or exit), with Reconnect starting a brand-new SSM session.
 */
export function VpnTerminalPage() {
  const { gatewayId } = useParams();
  // Environment from the opener's URL (?env=dev|prod); the popup has no selector of its own.
  const [searchParams] = useSearchParams();
  const env = envFromParam(searchParams.get('env'));
  const containerRef = useRef<HTMLDivElement | null>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const sessionRef = useRef<SsmTerminal | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  // Generation counter guarding against overlapping connect() calls (StrictMode double-mount, a
  // fast Reconnect double-click, or a gatewayId change mid-await): only the call that is still the
  // latest when its await resolves is allowed to touch termRef/sessionRef/state. A stale winner's
  // just-created AWS session is torn down immediately rather than left open until idle timeout.
  const connectSeqRef = useRef(0);
  const [state, setState] = useState<ConnState>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [filePushOpen, setFilePushOpen] = useState(false);

  const connect = useCallback(async () => {
    if (!gatewayId || !termRef.current) return;
    const seq = ++connectSeqRef.current;
    setState('connecting');
    setError(null);
    sessionRef.current?.close();
    try {
      const session = await vpnTerminalSession(env, gatewayId);
      if (seq !== connectSeqRef.current) {
        // Superseded (or unmounted) while awaiting — release the AWS session we just created and
        // leave the current (winning) connect's state untouched.
        vpnTerminalSessionClose(env, session.sessionId);
        return;
      }
      sessionIdRef.current = session.sessionId;
      const term = termRef.current;
      const ssmTerm = new SsmTerminal();
      sessionRef.current = ssmTerm;
      ssmTerm.connect({
        streamUrl: session.streamUrl,
        tokenValue: session.tokenValue,
        cols: term.cols,
        rows: term.rows,
        onOutput: (text) => {
          setState('connected');
          term.write(text);
        },
        onClose: (reason) => {
          setState('closed');
          term.write(`\r\n\x1b[33m${reason} — use Reconnect to start a new session.\x1b[0m\r\n`);
        },
      });
      termRef.current?.focus();
    } catch (e) {
      if (seq !== connectSeqRef.current) return; // stale attempt — don't clobber a newer one's state
      setState('closed');
      setError((e as Error).message);
    }
  }, [gatewayId]);

  // Terminal setup once; session lifecycle separately.
  useEffect(() => {
    if (!containerRef.current) return;
    const term = new Terminal({ cursorBlink: true, fontSize: 14, theme: { background: '#0f1b2a' } });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(containerRef.current);
    fit.fit();
    term.focus();
    termRef.current = term;
    fitRef.current = fit;
    term.onData((d) => sessionRef.current?.sendText(d));
    term.onResize(({ cols, rows }) => sessionRef.current?.resize(cols, rows));
    const onWindowResize = () => fit.fit();
    window.addEventListener('resize', onWindowResize);
    const onUnload = () => {
      if (sessionIdRef.current) vpnTerminalSessionClose(env, sessionIdRef.current);
    };
    window.addEventListener('beforeunload', onUnload);
    void connect();
    return () => {
      connectSeqRef.current++; // invalidate any in-flight connect() so it can't resurrect state post-unmount
      window.removeEventListener('resize', onWindowResize);
      window.removeEventListener('beforeunload', onUnload);
      sessionRef.current?.close();
      // In-app navigation away from this tab (no beforeunload) — terminate the AWS session too,
      // not just the client WebSocket, for parity with the beforeunload path.
      if (sessionIdRef.current) vpnTerminalSessionClose(env, sessionIdRef.current);
      term.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connect]);

  const stateIndicator =
    state === 'connected' ? (
      <StatusIndicator type="success">connected</StatusIndicator>
    ) : state === 'connecting' ? (
      <StatusIndicator type="in-progress">connecting</StatusIndicator>
    ) : (
      <StatusIndicator type="stopped">closed</StatusIndicator>
    );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#0f1b2a' }}>
      <Box padding="s">
        <Header
          variant="h2"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {stateIndicator}
              <Button iconName="upload" onClick={() => setFilePushOpen(true)}>
                Send file…
              </Button>
              <Button iconName="refresh" disabled={state === 'connecting'} onClick={() => void connect()}>
                Reconnect
              </Button>
            </SpaceBetween>
          }
        >
          <span style={{ color: '#fff' }}>Terminal — {gatewayId} ({env})</span>
        </Header>
        {error && (
          <Alert type="error" header="Could not open the session">
            {error}
          </Alert>
        )}
      </Box>
      <div ref={containerRef} style={{ flex: 1, padding: '0 8px 8px' }} />
      <VpnFilePushModal
        visible={filePushOpen}
        env={env}
        gatewayId={gatewayId ?? ''}
        onDismiss={() => setFilePushOpen(false)}
      />
    </div>
  );
}
